import { safeParse } from "@/utils/safeParse";
import { isNil, merge, omit, pick } from "lodash";
import getParent from "../../utils/getParent";
import reporter from "../report/reporter";
import { TrackerEvent } from "../shared/event";
import { getPinDetailData } from "../shared/getPinDetailData";
import { getSearchData } from "../shared/getSearchData";
import { getPage } from "../shared/global";
import { BEFORE_PAGE_CHANGE } from "../shared/variable";
import { getAttribute, getCommonData, getFilter } from "./get";

/**
 * 曝光数据获取
 * @param {Element} targetNode
 */
export function content_expose(targetNode) {
  const page = getPage();
  const filter = getFilter(page.page_nm);

  const trackData = {
    event_id: 33009,
    event_name: "content_expose",
    pin_id: targetNode.getAttribute("data-content-id"),
    ...page,
  };

  const { page_nm, sub_page_nm } = page;
  var sourceNode =
    (targetNode.hasAttribute("data-content-source") && targetNode) ||
    getParent(targetNode, "[data-content-source]");

  if (sourceNode) {
    if (filter.filter_one && filter.filter_one.length > 0) {
      trackData.pin_source = `${page_nm}_${sub_page_nm || ""}_${
        filter.filter_one[0]
      }_${sourceNode.getAttribute("data-content-source")}`;
    } else {
      trackData.pin_source = `${page_nm}_${
        sub_page_nm || ""
      }_${sourceNode.getAttribute("data-content-source")}`;
    }
  } else {
    if (filter.filter_one && filter.filter_one.length > 0) {
      trackData.pin_source = `${page_nm}_${sub_page_nm || ""}_${
        filter.filter_one[0]
      }`;
    } else {
      trackData.pin_source = `${page_nm}_${sub_page_nm || ""}`;
    }
  }

  // 仅在发现页且有 module_name="画板信息" 时，追加到 pin_source 以区分不同模块
  if (page_nm === "发现页") {
    const module_name = getAttribute(targetNode, "data-module-name");
    if (module_name === "画板信息") {
      trackData.pin_source = `${trackData.pin_source}_${module_name}`;
    }
  }

  const ext = getAttribute(targetNode, "data-ext");

  if (ext) {
    trackData.ext = safeParse(ext);
  }
  const contentType = getAttribute(targetNode, "data-content-type");
  if (contentType) {
    trackData.content_type = contentType;
  }

  const file_id = getAttribute(targetNode, "data-file-id");
  if (file_id) {
    trackData.ext = trackData.ext || {};
    trackData.ext.file_id = file_id;
  }

  if (
    page_nm === "搜索结果页" ||
    page_nm === "专题页" ||
    page_nm === "主题页" ||
    page_nm === "相似商用素材页" ||
    page_nm === "相似素材推荐页"
  ) {
    getSearchData(trackData);
  }

  if (page_nm === "专题页") {
    const el = document.querySelector("[data-topic-id]");
    if (el) {
      trackData.topic_title = el.getAttribute("data-topic-title");
      trackData.topic_id = el.getAttribute("data-topic-id");
    }
  }

  if (page_nm === "主题页" || page_nm === "发现页") {
    const el = document.querySelector("[data-sort-type]");
    if (el) {
      trackData.sort_type = el.getAttribute("data-sort-type");
    }
  }

  if (
    page.page_nm === "采集详情页" &&
    !isNil(getAttribute(targetNode, "data-pin-detail"))
  ) {
    getPinDetailData(trackData);
  }

  merge(trackData, filter);
  const search_word = getAttribute(targetNode, "data-search-word");
  if (search_word) {
    trackData.search_word = search_word;
  }

  if (page_nm === "采集详情页" && trackData.search_word) {
    getSearchData(trackData);
  }

  // 实时上下文特征：前置连续跳转内容 file_id 列表（用于埋点）
  const source_file_ids = getAttribute(targetNode, "data-source-file-ids");
  if (source_file_ids) {
    trackData.source_file_ids = source_file_ids.split(",");
  }

  getCommonData(targetNode, trackData);

  ContainerMng.add(trackData);
}

/**
 * 多容器管理器
 * 功能：页面切换时自动清除所有容器
 * 添加信息时，自动创建此模板类型的容器，下次遇到同模块数据，自动往容器里添加数据，不会新建容器
 **/
export class MutiMng {
  /**
   * @param {Object} option
   * @param {number} option.key 容器唯一key
   * @param {string} option.dataKey 放入容器的属性key
   * @param {{ids:string, number:string}} option.reporterKeys 放入容器的属性key
   * @param {Array<{key: string,reporterKey: string}>} option.extKeys 和ids一样需要数组上报的数据，需要在这指明
   */
  constructor({
    key = "pin_source",
    dataKey = "pin_id",
    reporterKeys,
    extKeys = [],
  } = {}) {
    /**
     * 容器集合
     * @type {Container[]}
     */
    this.containers = [];
    /**
     * 容器索引
     */
    this.keyMap = {};

    this.key = key;

    this.reporterKeys = reporterKeys;

    this.dataKey = dataKey;
    this.extKeys = extKeys;
    // pv埋点触发页面更改，然后触发容器更新
    TrackerEvent.on(BEFORE_PAGE_CHANGE, () => {
      this.clear();
    });
  }

  add(data) {
    // 如果存在 _aggregateKey，使用它作为聚合 key，否则使用默认的 key 字段
    const aggregateKey = data._aggregateKey || data[this.key];
    if (this.keyMap[aggregateKey] === undefined) {
      this.keyMap[aggregateKey] = this.containers.length;
      const container = new Container({
        dataKey: this.dataKey,
        reporterKeys: this.reporterKeys,
        extKeys: this.extKeys,
      });
      this.containers.push(container);
      container.push(data);
    } else {
      const index = this.keyMap[aggregateKey];
      this.containers[index].push(data);
    }
  }

  clear() {
    this.containers.forEach((container) => {
      container.submit();
    });
    this.containers = [];
    this.keyMap = {};
  }
}

const ContainerMng = new MutiMng({
  extKeys: [
    { key: "ext", reporterKey: "ext" },
    { key: "content_type", reporterKey: "content_types" },
  ],
});

/**
 * 埋点数据容器，为了曝光埋点而设计
 * 功能：达到容器容量时，自动提交
 * 达到时间时，自动提交
 * 容器为空时不启动定时，有数据时自动启动定时功能
 * 提交后重置容器，定时数据
 */
class Container {
  /**
   * 初始化参数
   * @param {Object} option
   * @param {number} option.delay 定时上报事件 单位 ms
   * @param {number} option.size 容器大小
   * @param {string} option.dataKey 放入容器的属性key
   * @param {Array<{key: string,reporterKey: string}>} option.extKeys 和ids一样需要数组上报的数据，需要在这指明
   * @param {{ids:string, number:string}} option.reporterKeys 放入容器的属性key
   */
  constructor({
    delay = 3000,
    size = 30,
    dataKey = "pin_id",
    extKeys = [],
    reporterKeys = {
      ids: "pin_ids",
      number: "pin_number",
    },
  } = {}) {
    this.delay = delay;
    this.size = size;
    this.data = new Set();
    this.extData = new Set();
    this.common = {};
    this.dataKey = dataKey;
    this.extKeys = extKeys;
    this.timer = null;
    this.reporterKeys = reporterKeys;
  }
  /**
   * 开启定时提交
   */
  time() {
    const that = this;
    this.timer = setTimeout(() => {
      that.submit();
    }, this.delay);
  }

  submit() {
    if (this.data.size === 0) return;
    // 数据上报
    const trackData = Object.assign({}, this.common, {
      [this.reporterKeys.ids]: [...this.data],
      [this.reporterKeys.number]: this.data.size,
    });

    // 额外批量数组字段上报
    this.extKeys.forEach((ext) => {
      const extData = [...this.extData].map((data) => data[ext.key]);
      // 过滤全空的
      if (extData.filter((data) => data).length) {
        trackData[ext.reporterKey || ext.key] = extData;
      }
    });

    reporter(trackData);

    // 提交后进行容器数据初始化
    this.data.clear();
    this.extData.clear();
    this.common = {};
    // 清除定时器，重置
    clearTimeout(this.timer);
    this.timer = null;
  }

  push(data) {
    const key = this.dataKey;
    const preSize = this.data.size;

    this.data.add(data[key]);

    // 避免重复上报的数据影响，如果重复上报了，会导致ext多一条数据，最终对应不上
    if (this.data.size > preSize) {
      this.extData.add(
        pick(
          data,
          this.extKeys.map((ext) => ext.key)
        )
      );
    }

    // 容器容量达到触发提交
    if (this.data.size === this.size) {
      this.submit();
    }
    // 当容器有数据了开启定时提交
    if (this.data.size === 1 && this.timer === null) {
      // 如果存在 _aggregateKey，说明使用了自定义聚合 key，需要恢复原始的 board_source
      // 同时移除临时的 _aggregateKey 和 _originalBoardSource 字段
      const commonData = {
        ...omit(data, [
          key,
          ...this.extKeys.map((ext) => ext.key),
          "_aggregateKey",
          "_originalBoardSource",
        ]),
      };
      if (data._aggregateKey && data._originalBoardSource) {
        // 恢复原始的 board_source
        commonData.board_source = data._originalBoardSource;
      }
      this.common = commonData;
      this.time();
    }
  }
}
